import { Earth } from '../earth/Earth';
import { Tower } from '../towers/Tower';
import { Pet } from '../pets/Pet';
import { getEquipmentById, EQUIPMENT_SLOTS, EquipmentType } from '../../data/EquipmentData';
import { Equipment, EquipmentManager } from '../equipment/Equipment';

export interface UserProfile {
  // Danh sách đã sở hữu
  ownedEarths: string[]; // keys của Earth đã mở khóa
  ownedTowers: string[]; // keys của Tower đã mở khóa
  ownedPets: string[]; // keys của Pet đã mở khóa
  ownedItems: any[]; // trang bị đã sở hữu (legacy, sẽ thay bằng equipment)

  // Danh sách đang trang bị (sử dụng trong game)
  equippedEarth: string; // key của Earth đang chọn
  equippedTowers: string[]; // keys của Tower có thể dùng trong game
  equippedPets: string[]; // keys của Pet có thể dùng trong game
  equippedItems: any[]; // trang bị đang mang (legacy)

  // Equipment System (mới)
  equipmentData: any[]; // Serialized equipment data

  // Currency
  gold: number;
  ruby: number;

  // Thiết lập
  settings?: any;

  // Tiến trình
  highestLevel: number; // level cao nhất từng đạt trong 1 ván
  totalXP: number; // legacy, giữ lại để không lỗi
  totalMonsterKills: number;
  totalBossKills: number;
  totalSessionTime: number;
  // Thêm hai trường mới cho profile tổng
  profileLevel: number; // level tổng tích lũy của user
  profileXP: number; // XP tổng tích lũy của user
}

// Global equipment manager instance
let globalEquipmentManager: EquipmentManager | null = null;

export function getEquipmentManager(): EquipmentManager {
  if (!globalEquipmentManager) {
    globalEquipmentManager = new EquipmentManager();
  }
  return globalEquipmentManager;
}

export function loadUserProfile(): UserProfile {
  const data = localStorage.getItem('userProfile');
  if (data) {
    const profile = JSON.parse(data);

    // Load equipment data nếu có
    if (profile.equipmentData) {
      const equipmentManager = getEquipmentManager();
      equipmentManager.deserialize(profile.equipmentData, getEquipmentById);
    }

    // Đảm bảo có trường profileLevel/profileXP
    if (profile.profileLevel === undefined) profile.profileLevel = 1;
    if (profile.profileXP === undefined) profile.profileXP = 0;
    return profile;
  }

  // Mặc định cho user mới
  return {
    ownedEarths: ['classic'],
    ownedTowers: ['laser'],
    ownedPets: ['laser'],
    ownedItems: [],
    equippedEarth: 'classic',
    equippedTowers: ['laser'],
    equippedPets: ['laser'],
    equippedItems: [],
    equipmentData: [],
    gold: 1000,
    ruby: 100,
    settings: {},
    highestLevel: 0,
    totalXP: 0,
    totalMonsterKills: 0,
    totalBossKills: 0,
    totalSessionTime: 0,
    profileLevel: 1,
    profileXP: 0,
  } as UserProfile;
}

export function saveUserProfile(profile: UserProfile) {
  // Serialize equipment data trước khi lưu
  const equipmentManager = getEquipmentManager();
  profile.equipmentData = equipmentManager.serialize();
  // Đảm bảo có trường profileLevel/profileXP
  if (profile.profileLevel === undefined) profile.profileLevel = 1;
  if (profile.profileXP === undefined) profile.profileXP = 0;
  localStorage.setItem('userProfile', JSON.stringify(profile));
}

// Hàm mua Earth
export function buyEarth(profile: UserProfile, earthKey: string, cost: number): boolean {
  if (profile.gold < cost) return false;
  if (profile.ownedEarths.includes(earthKey)) return false;

  profile.gold -= cost;
  profile.ownedEarths.push(earthKey);
  saveUserProfile(profile);
  return true;
}

// Hàm mua Tower
export function buyTower(profile: UserProfile, towerKey: string, cost: number): boolean {
  if (profile.gold < cost) return false;
  if (profile.ownedTowers.includes(towerKey)) return false;

  profile.gold -= cost;
  profile.ownedTowers.push(towerKey);
  saveUserProfile(profile);
  return true;
}

// Hàm mua Pet
export function buyPet(profile: UserProfile, petKey: string, cost: number): boolean {
  if (profile.gold < cost) return false;
  if (profile.ownedPets.includes(petKey)) return false;

  profile.gold -= cost;
  profile.ownedPets.push(petKey);
  saveUserProfile(profile);
  return true;
}

// Hàm mua Equipment
export function buyEquipment(profile: UserProfile, equipmentConfigId: string, cost: number, currency: 'gold' | 'ruby'): boolean {
  if (profile[currency] < cost) return false;

  const config = getEquipmentById(equipmentConfigId);
  if (!config) return false;

  // Trừ tiền
  profile[currency] -= cost;

  // Tạo equipment instance mới
  const instanceId = EquipmentManager.generateInstanceId(equipmentConfigId);
  const equipment = new Equipment(config, instanceId);

  // Thêm vào equipment manager
  const equipmentManager = getEquipmentManager();
  equipmentManager.addEquipment(equipment);

  saveUserProfile(profile);
  return true;
}

// Hàm nâng cấp Equipment
export function upgradeEquipment(profile: UserProfile, instanceId: string): boolean {
  const equipmentManager = getEquipmentManager();
  const equipment = equipmentManager.getEquipment(instanceId);

  if (!equipment || !equipment.canUpgrade()) return false;

  const cost = equipment.getUpgradeCost();
  const currency = equipment.config.currency;

  if (profile[currency] < cost) return false;

  // Trừ tiền và nâng cấp
  profile[currency] -= cost;
  equipment.upgrade();

  saveUserProfile(profile);
  return true;
}

// Hàm trang bị Equipment
export function equipEquipment(profile: UserProfile, instanceId: string, slotIndex?: number): boolean {
  const equipmentManager = getEquipmentManager();
  const equipment = equipmentManager.getEquipment(instanceId);

  if (!equipment) return false;

  const maxSlots = EQUIPMENT_SLOTS[equipment.config.type as keyof typeof EQUIPMENT_SLOTS] || 0;
  const currentEquipped = equipmentManager.getEquippedByType(equipment.config.type);

  // Kiểm tra slot limit
  if (currentEquipped.length >= maxSlots) {
    return false; // Cần unequip item khác trước
  }

  equipment.equip(slotIndex);
  saveUserProfile(profile);
  return true;
}

// Hàm bỏ trang bị Equipment
export function unequipEquipment(profile: UserProfile, instanceId: string): boolean {
  const equipmentManager = getEquipmentManager();
  const equipment = equipmentManager.getEquipment(instanceId);

  if (!equipment || !equipment.instance.isEquipped) return false;

  equipment.unequip();
  saveUserProfile(profile);
  return true;
}

// Hàm combine Equipment
export function combineEquipment(profile: UserProfile, instanceId1: string, instanceId2: string, instanceId3: string): boolean {
  const equipmentManager = getEquipmentManager();
  const eq1 = equipmentManager.getEquipment(instanceId1);
  const eq2 = equipmentManager.getEquipment(instanceId2);
  const eq3 = equipmentManager.getEquipment(instanceId3);

  if (!eq1 || !eq2 || !eq3) return false;

  const result = equipmentManager.combineEquipment(eq1, eq2, eq3);
  if (!result) return false;

  saveUserProfile(profile);
  return true;
}

// Hàm trang bị Earth
export function equipEarth(profile: UserProfile, earthKey: string): boolean {
  if (!profile.ownedEarths.includes(earthKey)) return false;

  profile.equippedEarth = earthKey;
  saveUserProfile(profile);
  return true;
}

// Hàm trang bị Tower
export function equipTower(profile: UserProfile, towerKey: string): boolean {
  if (!profile.ownedTowers.includes(towerKey)) return false;
  if (profile.equippedTowers.includes(towerKey)) return false;

  profile.equippedTowers.push(towerKey);
  saveUserProfile(profile);
  return true;
}

// Hàm bỏ trang bị Tower
export function unequipTower(profile: UserProfile, towerKey: string): boolean {
  const index = profile.equippedTowers.indexOf(towerKey);
  if (index === -1) return false;

  profile.equippedTowers.splice(index, 1);
  saveUserProfile(profile);
  return true;
}

// Hàm trang bị Pet
export function equipPet(profile: UserProfile, petKey: string): boolean {
  if (!profile.ownedPets.includes(petKey)) return false;
  if (profile.equippedPets.includes(petKey)) return false;

  profile.equippedPets.push(petKey);
  saveUserProfile(profile);
  return true;
}

// Hàm bỏ trang bị Pet
export function unequipPet(profile: UserProfile, petKey: string): boolean {
  const index = profile.equippedPets.indexOf(petKey);
  if (index === -1) return false;

  profile.equippedPets.splice(index, 1);
  saveUserProfile(profile);
  return true;
} 